import { forwardRef, useEffect, useRef } from "react";
import { useRecoilValue } from "recoil";
import classnames from "classnames";

import { SwarmMarketEvent } from "src/common/swarm/destructure";
import { useConfig } from "src/hooks/config";
import { useTranslation } from "src/hooks/translation";
import { ISelection } from "src/constants/interfaces/bet";
import { decimalString } from "src/constants/types";
import MarketBase from "../../../../common/components/marketBase";
import MarketChangeDropdown from "../marketChangeDropdown";
import EventPrice from "src/components/eventPrice";
import { useSelectionSubscription } from "src/hooks/selectionSubscription";
import { BetTypeEnum } from "src/constants/enums/betType";
import { RacingSportTypesIconsEnum } from "src/constants/enums/racingSportTypes";
import { GameTypeEnum, GameTypeViewEnum } from "src/constants/enums/gameType";
import mainStore from "src/stores/main";
import { emitGoToGame } from "src/services/communicator/actions";
import StakeInput from "src/common/components/stakeInput";
import CounterOfferToggle from "src/components/selectionsList/components/counterOfferToggle";

import "./style.scss";
import upperDataStore from "src/stores/upperData";
import { getEventName } from "src/common/helpers/eventName";
import selectionsStore, { storeChangeSelection } from "src/stores/selections";
import { bankerBetEnabled } from "src/rules/bankerBet";
import { toastWrapper } from "src/common/components/toastWrapper";
import Tooltip from "src/components/tooltip";
import { getEffectiveBankerCount, getEffectiveSelectionsCount } from "src/common/helpers/betBuilder";

interface IProps {
  selection: ISelection;
  isBoosted?: boolean;
  onChange(selection: ISelection): void;
  onRemove(eventId: number): void;
  onStakeChange(value: decimalString): void;
  onStakeApplyToAll(value: decimalString): void;
}

const Selection = forwardRef<HTMLDivElement, IProps>(
  ({ selection, onRemove, onChange, onStakeChange, onStakeApplyToAll }: IProps, ref) => {
    const t = useTranslation();
    const config = useConfig();
    const { event, game, market, inactive, hasError, isBoosted } = selection;
    const eventId = event.id;

    const { betType } = useRecoilValue(mainStore);
    const { selections } = useRecoilValue(selectionsStore);
    const { transformSelection } = useRecoilValue(upperDataStore);
    const bankerButtonRef = useRef<HTMLButtonElement | null>(null);

    const loading = useSelectionSubscription(selection);
    const hasBanker = bankerBetEnabled(config, betType, selections);

    const onMarketChange = (newEvent: SwarmMarketEvent) => onChange({ ...selection, ...newEvent, alternateOdd: 0 });

    const toggleBanker = () => {
      // For System+banker math each bet builder card counts as ONE leg —
      // counting raw selections here let the user mark too many bankers
      // (e.g. BB card with 2 legs + 2 simples, both simples banker → only
      // the BB card left as non-banker, an invalid system). That state
      // collapsed `selectOptions` to `[]` and triggered an infinite
      // setMainState loop in <SystemOptions>, freezing the app.
      const effectiveNonBanker =
        getEffectiveSelectionsCount(selections) - getEffectiveBankerCount(selections);
      if (!selection.isBanker && effectiveNonBanker < 3) {
        toastWrapper('error', 'At least 2 non-banker selections are required for banker bet');
      } else {
        storeChangeSelection(selection.event.id, { ...selection, isBanker: !selection.isBanker });
      }
    }

    const handleSelectionClick = () => {
      if (hasError) {
        storeChangeSelection(eventId, (oldSelection) => ({
          ...oldSelection,
          hasError: false,
        }));
      }
    };

    useEffect(() => {
      return () => {
        storeChangeSelection(eventId, (oldSelection) => ({ ...oldSelection, alternateOdd: 0, priceChangeType: null }));
      }
    }, [betType, eventId]);

    const sportAlias = game.sport_alias || game.sport?.alias || "";
    const regionAlias = game.region_alias || game.region?.alias || event.region?.alias || "";
    const goToGameHandler = () => {
      if (event.isRacingEvent || !!game.is_blocked || inactive) return;

      emitGoToGame({
        gameId: game.id,
        sportAlias,
        isRacingEvent: event.isRacingEvent,
        racingType: event.racingType,
        regionAlias,
        sportId: event.sport?.id || game.sportId,
        regionId: game.regionId || event.region?.id,
        competitionId: game.competition?.id || game.competitionId || event.competition?.id,
        marketId: Object.keys(game.market || {})[0],
        listType: market.display_key === "OUTRIGHT" ? "outrights" : "all",
        gameType: GameTypeViewEnum[game.is_live ? GameTypeEnum.Live : GameTypeEnum.Prematch],
      });
    };

    const transformedSelection = transformSelection(selection);

    return (
      <div
        ref={ref}
        onClick={handleSelectionClick}
        className={classnames("B-betslip-selection", {
          "loading-game-selection": loading,
          "inactive-game-selection": !!game.is_blocked,
          "error-game-selection": hasError,
        })}
      >
        {(!!game.is_blocked || inactive) && (
          <div className="inactive-selection-game">
            <i className="global-icon icon-blocked game-block-icon" />
            <span>{t("Event Is Inactive")}</span>
          </div>
        )}

        {loading && (
          <div>
            <i className="global-icon icon-Vector1 event-available" />
            <span className="available-text">{t("Event Is loading")}</span>
          </div>
        )}

        <div className="B-betslip-selection-title-wrp">
          <div className="B-betslip-section-name-wrp">
            <div className="B-betslip-section-icon-wrp">
              {sportAlias && (
                <span className={`B-sport-icon sport-icon sport-icon-${sportAlias}`} />
              )}
              {event.racingType && (
                <span
                  className={`B-sport-icon sport-icon sport-icon-${RacingSportTypesIconsEnum[event.racingType]}`}
                />
              )}
              {!!game.is_live && <span className="B-live" />}
            </div>
            <div className="B-betslip-section-txt" onClick={goToGameHandler}>
              <span className="B-betslip-team-name">{game.team1_name}</span>
              {game.team2_name && (
                <>
                  <span className="B-betslip-team-count">-</span>
                  <span className="B-betslip-team-name">{game.team2_name}</span>
                </>
              )}
            </div>
          </div>

          {hasBanker && (
            <>
              <button
                ref={bankerButtonRef}
                className={`B-betslip-selection-banker ${selection.isBanker ? 'B-betslip-selection-banker-active' : ''}`}
                onClick={toggleBanker}
              >
                {t('Banker')}
              </button>
              {bankerButtonRef.current && (
                <Tooltip
                  timeout={5000}
                  anchor={bankerButtonRef.current}
                  content={t('This selection must win. It is included in every system combination.')}
                />
              )}
            </>
          )}

          <div className="B-betslip-selection-title-btn">
            <button
              className="global-icon icon-Close"
              onClick={() => onRemove(event.id)}
            />
          </div>
        </div>

        <div className="B-selection-details">
          <div className="B-sel-det-match-res-wrp">
            {!event.isRacingEvent && (
              <p className="B-sel-match-res-txt">{transformedSelection.market.name}</p>
            )}
            <div className="B-sel-match-res-coef">
              {config.enableMarketChangeDropdown &&
                config.availableMarketTypesToChange.includes(market.display_key) ? (
                <MarketChangeDropdown
                  selection={selection}
                  onChange={onMarketChange}
                />
              ) : (
                <span className="B-market">
                  {getEventName(t, config, event, game)} <MarketBase base={event.base} />
                </span>
              )}
            </div>
            {selection.event.optionViewLabel && (
              <span className="B-racing-event-option">
                {selection.event.optionViewLabel}
              </span>
            )}
            {event.price &&
              !event.sp &&
              !event.eventOption &&
              !isNaN(event.price) && (
                <EventPrice isBoosted={isBoosted} price={event.price} />
              )}
            {event.sp && <span className="B-betslip-event-price">{t("SP")}</span>}
          </div>

          {selection.loaded && <CounterOfferToggle selection={selection} />}

          {betType === BetTypeEnum.Single && config.enableSingleStakeInput && (
            <div className="B-sel-det-inp-wrp">
              <StakeInput
                value={selection.singleStake || ""}
                onChange={onStakeChange}
              />
              <div className="B-sel-det-btn-block">
                <button
                  className="global-icon icon-double-down-arrow"
                  onClick={() => onStakeApplyToAll(selection.singleStake || "")}
                  disabled={!selection.singleStake}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default Selection;
